import * as React from 'react';
import { action, IObservableArray, observable, observe } from 'mobx';
import { inject, observer } from 'mobx-react';

// Services
import { IGetUserByTagsResponse, getUserByTags } from '../../services/relation';

// Types
import { IStore } from '../../store/Store';
export type IPeopleItem = IGetUserByTagsResponse;
export interface ISelectablePeopleItem extends IGetUserByTagsResponse {
  selected: boolean;
}
import { ITagQuery } from '../AppNetworkPeople/AppNetworkPeople';

// Components
import Container from '../../components/Container';
import Icon from '../../components/Icon';
import { FixedFAB, FixedTopBar } from '../../components/Layout';

import PeopleList from './PeopleList';
import CreateQuestionToolbar from './CreateQuestionToolbar';
import SelectablePeopleList from './SelectablePeopleList';
import UserDialog from './UserDialog';
import QuestionDialog from './QuestionDialog';

//
interface IProps {
  store?: IStore;
  tagQuery: ITagQuery[];
}

class AppNetworkPeople extends React.Component<IProps, {}> {
  @observable
  userModalState: { open: boolean; userId: string | null } = {
    open: false,
    userId: null
  };
  @observable questionModalState: { open: boolean } = { open: false };

  @observable selectable: boolean = false;
  @observable.shallow peopleResults: IPeopleItem[] | null = null;
  @observable selectablePeopleResults: ISelectablePeopleItem[] | null = null;
  @observable.shallow selectedPeople: ISelectablePeopleItem[] = [];

  tagQueryDisposer: () => void;

  componentDidMount() {
    this.getPeopleResults();
    this.tagQueryDisposer = observe(this.props.tagQuery, this.getPeopleResults);
  }

  @action
  getPeopleResults = async () => {
    this.peopleResults = await getUserByTags({
      params: {
        networkId: this.props.store!.networkStore.network!.networkId
      },
      query: {
        ids: this.props.tagQuery.map(item => item.tagId).join(',')
      }
    });
    if (this.selectable) {
      if (this.selectedPeople.length === 0) {
        this.selectablePeopleResults = this.peopleResults.map((item, i) => ({
          ...item,
          selected: false
        }));
      } else {
        this.selectablePeopleResults = this.peopleResults.map((item, i) => {
          const found =
            this.selectedPeople.findIndex(
              target => target.userId === item.userId
            ) !== -1;
          return { ...item, selected: found };
        });
      }
    }
  };

  @action
  handlePeopleToolbarQuestion = () => {
    if (this.peopleResults) {
      this.selectablePeopleResults = this.peopleResults.map((item, i) => ({
        ...item,
        selected: false
      }));
    }
    this.selectable = true;
  };

  @action
  handleQuestionToolbarCancel = () => {
    this.selectable = false;
    this.selectedPeople = [];
    this.selectablePeopleResults = null;
  };

  @action
  handleQuestionToolbarCreate = () => {
    this.questionModalState.open = true;
  };

  @action
  handlePeopleSelect = (item: IPeopleItem) => () => {
    this.userModalState.open = true;
    this.userModalState.userId = item.userId;
  };

  @action
  handleUserModalRequestClose = () => {
    this.userModalState.open = false;
  };

  @action
  handleUserModalExited = () => {
    this.userModalState.userId = null;
  };

  @action
  handleQuestionModalRequestClose = () => {
    this.questionModalState.open = false;
  };

  @action
  handleSelectablePeopleSelect = (item: ISelectablePeopleItem) => () => {
    // The selected flag and the SelectedPeople Array must be kept in sync in order to gain some performance
    if (item.selected === false) {
      this.selectedPeople.push(item);
    } else {
      (this.selectedPeople as IObservableArray<ISelectablePeopleItem>).remove(
        item
      );
    }
    item.selected = !item.selected;
  };

  @action
  handleQuestionDialogRemoveSelectedPeople = (item: ISelectablePeopleItem) => {
    // The selectedPeople and the selectablePeopleResults Array must be kept in sync in order to gain some performance
    (this.selectedPeople as IObservableArray<ISelectablePeopleItem>).remove(
      item
    );
    item.selected = !item.selected;

    if (this.selectablePeopleResults) {
      const foundItem = this.selectablePeopleResults.find(
        value => value.userId === item.userId
      );
      if (foundItem) {
        foundItem.selected = false;
      }
    }
  };

  @action
  handleQuestionDialogCreated = () => {
    this.selectable = false;
    this.selectedPeople = [];
    this.selectablePeopleResults = null;
    this.handleQuestionModalRequestClose();
  };

  componentWillUnmount() {
    this.tagQueryDisposer();
  }

  render() {
    const role = this.props.store!.networkStore.person!.role;
    return (
      <div>
        <Container size="lg" padded={true}>
          {this.selectable ? (
            <SelectablePeopleList
              people={this.selectablePeopleResults}
              onSelect={this.handleSelectablePeopleSelect}
            />
          ) : (
            <PeopleList
              people={this.peopleResults}
              onSelect={this.handlePeopleSelect}
            />
          )}
        </Container>
        {this.selectable ? (
          <FixedFAB
            icon={<Icon>close</Icon>}
            tooltipTitle={'Cancel'}
            onClick={this.handleQuestionToolbarCancel}
          />
        ) : (
          <FixedFAB
            icon={<Icon>message</Icon>}
            tooltipTitle="Create&nbsp;Question"
            onClick={
              !this.selectable
                ? this.handlePeopleToolbarQuestion
                : this.handleQuestionToolbarCancel
            }
          />
        )}

        {this.selectable ? (
          <FixedTopBar size="lg" horizontalPadded={true} placement="top">
            <CreateQuestionToolbar
              selectedPeople={this.selectedPeople.length}
              onCreate={this.handleQuestionToolbarCreate}
              onCancel={this.handleQuestionToolbarCancel}
            />
          </FixedTopBar>
        ) : null}
        <UserDialog
          networkId={this.props.store!.networkStore.network!.networkId}
          state={this.userModalState}
          onClose={this.handleUserModalRequestClose}
          userRole={role}
        />
        <QuestionDialog
          networkId={this.props.store!.networkStore.network!.networkId}
          state={this.questionModalState}
          onClose={this.handleQuestionModalRequestClose}
          selectedPeople={this.selectedPeople}
          onPeopleRemove={this.handleQuestionDialogRemoveSelectedPeople}
          onCreated={this.handleQuestionDialogCreated}
        />
      </div>
    );
  }
}

export default inject('store')(observer(AppNetworkPeople));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/AppNetworkPeopleSearch/AppNetworkPeopleSearch.tsx